/**
 * RateLockAdvisorModal — MUI Dialog with the full Rate Lock Advisor dashboard.
 * Opened by clicking the RateLockBadge in the header.
 */
import { useState, useMemo } from 'react';
import {
  Alert, Box, Button, Card, CardContent, CardHeader, Chip, CircularProgress,
  Dialog, DialogActions, DialogContent, DialogTitle, Grid, IconButton,
  LinearProgress, MenuItem, Stack, Table, TableBody, TableCell, TableRow,
  TextField, Tooltip, Typography,
} from '@mui/material';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import RefreshIcon from '@mui/icons-material/Refresh';
import LockIcon from '@mui/icons-material/Lock';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import TrendingFlatIcon from '@mui/icons-material/TrendingFlat';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as RTooltip, ResponsiveContainer, ReferenceLine } from 'recharts';
import marketTrendApi from '../../../services/marketTrendApi';
import SectionHeader from '../../../components/shared/SectionHeader';

const fmt = (v, dec = 2) => v != null ? Number(v).toFixed(dec) : '—';

const RateLockAdvisorModal = ({ open, onClose }) => {
  const [closeDays, setCloseDays] = useState(30);
  const [tolerance, setTolerance] = useState('moderate');

  const { data, isLoading, isFetching, error, refetch, fulfilledTimeStamp } =
    marketTrendApi.endpoints.getMarketTrends.useQuery(
      { loanCloseDays: closeDays, riskTolerance: tolerance },
      { pollingInterval: open ? 5 * 60 * 1000 : 0, skip: !open }
    );

  const analysis = data?.analysis;
  const recommendation = data?.recommendation;

  const trendArrow = (direction) => {
    if (direction === 'UP') return <Typography variant="body2" component="span" color="error.main"><TrendingUpIcon sx={{ fontSize: 14, mr: 0.5 }} />Rising</Typography>;
    if (direction === 'DOWN') return <Typography variant="body2" component="span" color="success.main"><TrendingDownIcon sx={{ fontSize: 14, mr: 0.5 }} />Falling</Typography>;
    return <Typography variant="body2" component="span" color="text.secondary"><TrendingFlatIcon sx={{ fontSize: 14, mr: 0.5 }} />Flat</Typography>;
  };

  const volBadge = (label) => {
    const colorMap = { HIGH: 'error', MODERATE: 'warning', LOW: 'success' };
    return <Chip label={label} size="small" color={colorMap[label] || 'default'} variant="outlined" sx={{ height: 20, fontSize: '0.7rem' }} />;
  };

  const isLock = recommendation?.recommendation === 'LOCK';
  const confidencePct = Math.round((recommendation?.confidenceScore || 0) * 100);
  const lastRefresh = fulfilledTimeStamp ? new Date(fulfilledTimeStamp) : null;

  // Chart data
  const treasuryChart = useMemo(() => (data?.treasuryHistory || [])
    .filter(o => { const c = new Date(); c.setDate(c.getDate() - 30); return new Date(o.date) >= c; })
    .map(o => ({ date: new Date(o.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }), yield: o.value })),
    [data?.treasuryHistory]);

  const mortgageChart = useMemo(() => (data?.mortgageHistory || [])
    .filter(o => { const c = new Date(); c.setDate(c.getDate() - 90); return new Date(o.date) >= c; })
    .map(o => ({ date: new Date(o.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }), rate: o.value })),
    [data?.mortgageHistory]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xl" fullWidth scroll="paper">
      <DialogTitle>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Stack direction="row" alignItems="center" spacing={1}>
            <ShowChartIcon />
            <Typography variant="h6" component="span">Rate Lock Advisor</Typography>
            {recommendation && (
              <Chip
                label={`${isLock ? 'LOCK' : 'FLOAT'} — ${confidencePct}%`}
                size="small"
                color={isLock ? 'error' : 'success'}
                variant="outlined"
              />
            )}
          </Stack>
          <Stack direction="row" alignItems="center" spacing={1}>
            {isFetching && <CircularProgress size={18} />}
            {lastRefresh && (
              <Typography variant="caption" color="text.secondary">{lastRefresh.toLocaleTimeString()}</Typography>
            )}
            <IconButton size="small" onClick={refetch} disabled={isFetching} title="Refresh">
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Stack>
        </Stack>
      </DialogTitle>

      <DialogContent dividers>
        {error && !recommendation && (
          <Alert severity="error" sx={{ mb: 2 }}>Failed to load market data.</Alert>
        )}

        {recommendation && (
          <Grid container spacing={3}>
            {/* Left column */}
            <Grid size={{ xs: 12, lg: 5 }}>
              {/* Recommendation */}
              <Card variant="outlined" sx={{ mb: 2, borderColor: isLock ? 'error.main' : 'success.main' }}>
                <CardContent sx={{ textAlign: 'center', py: 3 }}>
                  <Typography variant="overline" color="text.secondary" fontWeight={700}>Market Direction</Typography>
                  <Typography variant="h5" sx={{ mb: 2 }}>{recommendation.marketDirection}</Typography>
                  <Box
                    sx={{
                      display: 'inline-flex', alignItems: 'center', px: 4, py: 1.5, borderRadius: 2, mb: 2,
                      bgcolor: isLock ? 'error.main' : 'success.main', color: 'white',
                      fontSize: '1.5rem', fontWeight: 800, letterSpacing: 2,
                    }}
                  >
                    {isLock ? <LockIcon sx={{ mr: 1 }} /> : <LockOpenIcon sx={{ mr: 1 }} />}
                    {isLock ? 'LOCK' : 'FLOAT'}
                  </Box>
                  <Box sx={{ mb: 2 }}>
                    <LinearProgress
                      variant="determinate"
                      value={confidencePct}
                      color={isLock ? 'error' : 'success'}
                      sx={{ height: 6, borderRadius: 3 }}
                    />
                    <Typography variant="body2" fontWeight={700} sx={{ mt: 0.5 }}>Confidence: {confidencePct}%</Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary">{recommendation.explanation}</Typography>
                </CardContent>
              </Card>

              {/* Controls */}
              <Card variant="outlined" sx={{ mb: 2 }}>
                <CardContent sx={{ py: 1.5 }}>
                  <Grid container spacing={2}>
                    <Grid size={{ xs: 6 }}>
                      <TextField
                        label="Days to Close"
                        type="number"
                        size="small"
                        fullWidth
                        value={closeDays}
                        inputProps={{ min: 1, max: 180 }}
                        onChange={(e) => setCloseDays(Math.max(1, parseInt(e.target.value) || 30))}
                      />
                    </Grid>
                    <Grid size={{ xs: 6 }}>
                      <TextField
                        select
                        label="Risk Tolerance"
                        size="small"
                        fullWidth
                        value={tolerance}
                        onChange={(e) => setTolerance(e.target.value)}
                      >
                        <MenuItem value="low">Low</MenuItem>
                        <MenuItem value="moderate">Moderate</MenuItem>
                        <MenuItem value="high">High</MenuItem>
                      </TextField>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>

              {/* Market Indicators */}
              <Card variant="outlined">
                <CardHeader
                  title={<SectionHeader title="Market Indicators" icon={<ShowChartIcon fontSize="small" />} sx={{ mb: 0 }} />}
                  sx={{ py: 1 }}
                />
                <Table size="small" sx={{ '& td': { fontSize: '0.8rem' } }}>
                  <TableBody>
                    <TableRow>
                      <TableCell sx={{ color: 'text.secondary' }}>10yr Treasury</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700 }}>{analysis?.treasury?.latest != null ? `${fmt(analysis.treasury.latest)}%` : '—'}</TableCell>
                      <TableCell align="right">{trendArrow(recommendation.treasuryTrend)}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ color: 'text.secondary' }}>30yr Mortgage</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700 }}>{analysis?.mortgage?.latest != null ? `${fmt(analysis.mortgage.latest)}%` : '—'}</TableCell>
                      <TableCell align="right">{trendArrow(recommendation.mortgageTrend)}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ color: 'text.secondary' }}>Fed Funds Rate</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700 }}>{analysis?.fedFunds?.latest != null ? `${fmt(analysis.fedFunds.latest)}%` : '—'}</TableCell>
                      <TableCell align="right">{trendArrow(analysis?.fedFunds?.direction30d)}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ color: 'text.secondary' }}>CPI (Inflation)</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700 }}>{analysis?.inflation?.latest != null ? fmt(analysis.inflation.latest, 1) : '—'}</TableCell>
                      <TableCell align="right">{trendArrow(analysis?.inflation?.direction30d)}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ color: 'text.secondary' }}>Volatility</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700 }}>{recommendation.volatility != null ? Number(recommendation.volatility).toFixed(3) : '—'}</TableCell>
                      <TableCell align="right">{volBadge(recommendation.volatilityLabel)}</TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </Card>
            </Grid>

            {/* Right column: Charts */}
            <Grid size={{ xs: 12, lg: 7 }}>
              <Card variant="outlined" sx={{ mb: 2 }}>
                <CardHeader
                  title={<SectionHeader title="10-Year Treasury — 30 Days" icon={<ShowChartIcon fontSize="small" />} sx={{ mb: 0 }} />}
                  sx={{ py: 1 }}
                />
                <CardContent sx={{ py: 1 }}>
                  {treasuryChart.length > 0 ? (
                    <ResponsiveContainer width="100%" height={220}>
                      <LineChart data={treasuryChart} margin={{ top: 5, right: 15, left: -10, bottom: 5 }}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="date" tick={{ fontSize: 10 }} interval={Math.max(0, Math.floor(treasuryChart.length / 7))} />
                        <YAxis tick={{ fontSize: 10 }} domain={['auto', 'auto']} tickFormatter={(v) => `${v.toFixed(2)}%`} />
                        <RTooltip formatter={(v) => [`${Number(v).toFixed(3)}%`, 'Yield']} contentStyle={{ fontSize: 11, borderRadius: 6 }} />
                        {analysis?.treasury?.latest && (
                          <ReferenceLine y={analysis.treasury.latest} stroke="#1a1a1a" strokeDasharray="5 5" />
                        )}
                        <Line type="monotone" dataKey="yield" stroke={isLock ? '#d32f2f' : '#2e7d32'} strokeWidth={2} dot={false} activeDot={{ r: 3 }} />
                      </LineChart>
                    </ResponsiveContainer>
                  ) : (
                    <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>No data</Typography>
                  )}
                </CardContent>
              </Card>

              <Card variant="outlined">
                <CardHeader
                  title={<SectionHeader title="30-Year Mortgage Rate — 90 Days" icon={<ShowChartIcon fontSize="small" />} sx={{ mb: 0 }} />}
                  sx={{ py: 1 }}
                />
                <CardContent sx={{ py: 1 }}>
                  {mortgageChart.length > 0 ? (
                    <ResponsiveContainer width="100%" height={200}>
                      <LineChart data={mortgageChart} margin={{ top: 5, right: 15, left: -10, bottom: 5 }}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="date" tick={{ fontSize: 10 }} interval={Math.max(0, Math.floor(mortgageChart.length / 7))} />
                        <YAxis tick={{ fontSize: 10 }} domain={['auto', 'auto']} tickFormatter={(v) => `${v.toFixed(2)}%`} />
                        <RTooltip formatter={(v) => [`${Number(v).toFixed(3)}%`, 'Rate']} contentStyle={{ fontSize: 11, borderRadius: 6 }} />
                        <Line type="monotone" dataKey="rate" stroke="#0288d1" strokeWidth={2} dot={false} activeDot={{ r: 3 }} />
                      </LineChart>
                    </ResponsiveContainer>
                  ) : (
                    <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>No data</Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        )}

        {isLoading && !recommendation && (
          <Box sx={{ textAlign: 'center', py: 5 }}>
            <CircularProgress sx={{ mb: 2 }} />
            <Typography>Loading market data...</Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ justifyContent: 'space-between', px: 2 }}>
        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
          <InfoOutlinedIcon sx={{ fontSize: 12, mr: 0.5 }} />
          Rate recommendations are informational only and do not constitute financial advice. Data from FRED API.
        </Typography>
        <Button variant="contained" size="small" onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default RateLockAdvisorModal;
